import { formatPrice } from '../utils/formatPrice';
import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { Search, ChevronLeft, ChevronRight, X, Package } from 'lucide-react';
import { adminApi } from '../api/admin';
import AdminLayout from '../components/AdminLayout';

export default function AdminOrders() {
  usePageTitle("Administration — Gestion des commandes — Lodiss");

  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadOrders();
  }, [page, search, statusFilter, typeFilter]);

  const loadOrders = () => {
    setLoading(true);
    adminApi.getOrders({ 
      page, 
      limit: 20, 
      search: search || undefined,
      status: statusFilter || undefined,
      type: typeFilter || undefined,
    })
      .then(res => {
        setOrders(res.items);
        setTotal(res.total);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const handleStatusChange = async (status: string) => {
    if (!selectedOrder) return;
    setSaving(true);
    try {
      await adminApi.updateOrderStatus(selectedOrder.id, { 
        status, 
        tracking_number: trackingNumber || undefined 
      });
      setSelectedOrder(null);
      setTrackingNumber('');
      loadOrders();
    } catch (err) {
      console.error('Failed to update order:', err);
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-amber-50 text-amber-700';
      case 'confirmed': return 'bg-emerald-50 text-emerald-700';
      case 'processing': return 'bg-blue-50 text-blue-700';
      case 'shipped': return 'bg-indigo-50 text-indigo-700';
      case 'delivered': return 'bg-gray-50 text-gray-700';
      case 'cancelled': return 'bg-red-50 text-red-700';
      default: return 'bg-gray-50 text-gray-700';
    }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <AdminLayout>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Commandes</h1>

        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg"
            />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="px-4 py-2 border rounded-lg">
            <option value="">Tous statuts</option>
            <option value="pending">En attente</option>
            <option value="confirmed">Confirmée</option>
            <option value="processing">En cours</option>
            <option value="shipped">Expédiée</option>
            <option value="delivered">Livrée</option>
            <option value="cancelled">Annulée</option>
          </select>
          <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} className="px-4 py-2 border rounded-lg">
            <option value="">Tous types</option>
            <option value="direct">Direct</option>
            <option value="quote">Devis</option>
          </select>
        </div>

        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">N° Commande</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Client</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Articles</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center"><div className="w-6 h-6 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin mx-auto" /></td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Aucune commande</td></tr>
              ) : (
                orders.map(order => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm">{order.order_number}</td>
                    <td className="px-4 py-3 text-sm">{order.user?.full_name || '-'}</td>
                    <td className="px-4 py-3 text-sm">{order.type === 'direct' ? 'Direct' : 'Devis'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(order.status)}`}>{order.status}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">{order.items?.length || 0}</td>
                    <td className="px-4 py-3 text-sm font-medium">{formatPrice(order.total_amount || 0)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString('fr-FR')}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setSelectedOrder(order)} className="text-sm text-lodiss-red hover:underline">Voir</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-gray-500">{(page - 1) * 20 + 1} - {Math.min(page * 20, total)} sur {total}</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 border rounded-lg disabled:opacity-50"><ChevronLeft size={18} /></button>
              <span className="text-sm">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 border rounded-lg disabled:opacity-50"><ChevronRight size={18} /></button>
            </div>
          </div>
        )}

        {selectedOrder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-bold">Commande {selectedOrder.order_number}</h2>
                <button onClick={() => setSelectedOrder(null)} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Client</p>
                    <p className="font-medium">{selectedOrder.user?.full_name}</p>
                    <p className="text-sm text-gray-500">{selectedOrder.user?.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Statut</p>
                    <select
                      value={selectedOrder.status}
                      onChange={e => { setSelectedOrder({ ...selectedOrder, status: e.target.value }); }}
                      className="mt-1 w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="pending">En attente</option>
                      <option value="confirmed">Confirmée</option>
                      <option value="processing">En cours</option>
                      <option value="shipped">Expédiée</option>
                      <option value="delivered">Livrée</option>
                      <option value="cancelled">Annulée</option>
                    </select>
                  </div>
                </div>
                {selectedOrder.status === 'shipped' && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Numéro de suivi</p>
                    <input
                      type="text"
                      value={trackingNumber}
                      onChange={e => setTrackingNumber(e.target.value)}
                      placeholder="Numéro de suivi..."
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-500 mb-2">Articles</p>
                  <div className="space-y-2">
                    {(selectedOrder.items || []).map((item: any, i: number) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <Package size={20} className="text-gray-400" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">Produit #{item.product_id}</p>
                          <p className="text-xs text-gray-500">Qté: {item.quantity}</p>
                        </div>
                        <p className="text-sm font-medium">{formatPrice(item.total || 0)}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-right text-lg font-bold">
                  Total: {formatPrice(selectedOrder.total_amount || 0)}
                </div>
              </div>
              <div className="flex justify-end gap-3 p-4 border-t">
                <button onClick={() => setSelectedOrder(null)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Fermer</button>
                <button
                  onClick={() => handleStatusChange(selectedOrder.status)}
                  disabled={saving}
                  className="px-4 py-2 bg-lodiss-red text-white rounded-lg hover:bg-lodiss-dark disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : 'Mettre à jour'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
